import { Info, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import React from 'react';
import { focusTooltipInEnd } from '../../utility/helpers/commonFunction';

/**
 * InputWithIcon component
 * @param {{type: string, disabled: boolean, value: number|string, onChange: (value: number|string) => void, min: number, max: number, error: boolean, helperText: string, onErrorIconClick: () => void}} props
 * `error` shows a red border, a filled info icon in place of the arrows (clicking it calls `onErrorIconClick`) and `helperText` below.
 * @returns {JSX.Element}
 */
const InputWithIcon = ({
    inputRef = null,
    outerIndex = null,
    innerIndex = null,
    type = 'qty',
    disabled = false,
    hideEndAdornment = false,
    skipOnChange = false,
    value,
    onChange,
    min = 0,
    max = Number.MAX_SAFE_INTEGER,
    borderRadius = '8px',
    fontSize = '16px',
    error = false,
    helperText = '',
    onErrorIconClick = null,
}) => {
    const parsedValue = type === 'cost' && typeof value === 'string' ? Number(value.replace(/[^\d.]/g, '')) : Number(value);

    const getStep = (val) => {
        const str = String(val);
        if (!str.includes('.')) return 1;

        const decimals = str.split('.')[1].length;
        return Number(`0.${'0'.repeat(decimals - 1)}1`);
    };

    const handleIncrement = () => {
        if (disabled) return;

        const step = getStep(parsedValue);
        const newValue = Number((parsedValue + step).toFixed(2));

        if (newValue <= max) {
            onChange(newValue);
        }
    };

    const handleDecrement = () => {
        if (disabled) return;

        const step = getStep(parsedValue);
        const newValue = Number((parsedValue - step).toFixed(2));

        if (newValue >= min) {
            onChange(newValue);
        }
    };

    return (
        <TextField
            inputRef={
                inputRef
                    ? (el) => {
                          if (typeof innerIndex === 'number' && innerIndex >= 0) {
                              // Use composite key for nested refs (e.g., parts within services)
                              inputRef.current[`${outerIndex}-${innerIndex}`] = el;
                          } else if (typeof outerIndex === 'number' && outerIndex >= 0) {
                              // Use single index for flat refs
                              inputRef.current[outerIndex] = el;
                          }
                      }
                    : null
            }
            fullWidth
            disabled={disabled}
            variant="outlined"
            value={value}
            error={error}
            helperText={error ? helperText : undefined}
            onFocus={focusTooltipInEnd}
            onChange={(event) => {
                if (skipOnChange) {
                    const inputValue = event.target.value;
                    const numericValue = inputValue.replace(/[^\d.]/g, '');
                    onChange(numericValue);
                    return;
                }
                const inputValue = event.target.value;
                const numericValue = Number(inputValue.replace(/[^\d.]/g, ''));
                onChange(numericValue);
            }}
            onKeyDown={(event) => {
                if (event.key === 'ArrowUp') {
                    handleIncrement();
                }
                if (event.key === 'ArrowDown') {
                    handleDecrement();
                }
            }}
            InputProps={{
                endAdornment: hideEndAdornment ? null : error ? (
                    <InputAdornment position="end">
                        <Info
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={onErrorIconClick || undefined}
                            sx={{ color: 'error.main', cursor: onErrorIconClick ? 'pointer' : 'default', mr: '8px' }}
                        />
                    </InputAdornment>
                ) : (
                    <InputAdornment position="end">
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '24px',
                            }}
                        >
                            <KeyboardArrowUp
                                fontSize="small"
                                onClick={!disabled ? handleIncrement : undefined}
                                style={{
                                    cursor: disabled ? 'default' : 'pointer',
                                    height: '15px',
                                    color: '#000',
                                    opacity: disabled ? 0.3 : 0.56,
                                }}
                            />
                            <KeyboardArrowDown
                                fontSize="small"
                                onClick={!disabled ? handleDecrement : undefined}
                                style={{
                                    cursor: disabled ? 'default' : 'pointer',
                                    height: '15px',
                                    color: '#000',
                                    opacity: disabled ? 0.3 : 0.56,
                                }}
                            />
                        </div>
                    </InputAdornment>
                ),
                autoComplete: 'off',
            }}
            sx={
                // disabled
                //     ? MuiDisabledTextField
                //     :
                {
                    height: '40px',
                    boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                    borderRadius: borderRadius,
                    // The helper text hangs under the box without taking height, so the box stays level with
                    // the inputs beside it; the equal margins keep it centred and leave room for the text.
                    ...(error && helperText ? { my: '20px' } : {}),
                    '& .MuiFormHelperText-root': {
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        m: '2px 0 0',
                        fontSize: '14px',
                        lineHeight: '18px',
                        whiteSpace: 'nowrap',
                    },
                    '& .MuiInputBase-root': {
                        border: 'none',
                        padding: '12px 0 12px 0',
                        height: '40px',
                        fontSize: fontSize,
                    },
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                        borderRadius: borderRadius,
                    },
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                    },
                    '& input[type=number]': {
                        MozAppearance: 'textfield',
                    },
                }
            }
        />
    );
};

export default InputWithIcon;
